// src/components/map/PlacesAutocomplete.tsx
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Autocomplete } from '@react-google-maps/api';
import { MapPin, X, Search } from 'lucide-react';

export interface PlaceResult {
  formattedAddress: string;
  location: {
    lat: number;
    lng: number;
  };
  placeId: string;
  addressComponents?: {
    streetNumber?: string;
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
}

interface PlacesAutocompleteProps {
  onPlaceSelect: (place: PlaceResult) => void;
  placeholder?: string;
  defaultValue?: string;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  bounds?: google.maps.LatLngBoundsLiteral;
  countryRestriction?: string | string[];
  types?: string[];
  clearOnSelect?: boolean;
  showIcon?: boolean;
}

const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = ({
  onPlaceSelect,
  placeholder = 'Search for an address...',
  defaultValue = '',
  className = '',
  inputClassName = '',
  disabled = false,
  bounds,
  countryRestriction = 'us',
  types = ['address'],
  clearOnSelect = false,
  showIcon = true,
}) => {
  const [inputValue, setInputValue] = useState(defaultValue);
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleLoad = useCallback((autocompleteInstance: google.maps.places.Autocomplete) => {
    setAutocomplete(autocompleteInstance);
  }, []);

  const handlePlaceChanged = useCallback(() => {
    if (!autocomplete) return;

    const place = autocomplete.getPlace();

    if (!place.geometry?.location) {
      console.warn('No geometry for selected place');
      return;
    }

    const addressComponents: PlaceResult['addressComponents'] = {};

    if (place.address_components) {
      for (const component of place.address_components) {
        const types = component.types;

        if (types.includes('street_number')) {
          addressComponents.streetNumber = component.long_name;
        }
        if (types.includes('route')) {
          addressComponents.street = component.long_name;
        }
        if (types.includes('locality')) {
          addressComponents.city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          addressComponents.state = component.short_name;
        }
        if (types.includes('postal_code')) {
          addressComponents.zipCode = component.long_name;
        }
        if (types.includes('country')) {
          addressComponents.country = component.long_name;
        }
      }
    }

    const result: PlaceResult = {
      formattedAddress: place.formatted_address || '',
      location: {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      },
      placeId: place.place_id || '',
      addressComponents,
    };

    onPlaceSelect(result);

    if (clearOnSelect) {
      setInputValue('');
    } else {
      setInputValue(result.formattedAddress);
    }
  }, [autocomplete, onPlaceSelect, clearOnSelect]);

  const handleClear = useCallback(() => {
    setInputValue('');
    inputRef.current?.focus();
  }, []);

  const autocompleteOptions: google.maps.places.AutocompleteOptions = {
    types,
    componentRestrictions: countryRestriction
      ? { country: Array.isArray(countryRestriction) ? countryRestriction : [countryRestriction] }
      : undefined,
    bounds: bounds
      ? new google.maps.LatLngBounds(
          { lat: bounds.south, lng: bounds.west },
          { lat: bounds.north, lng: bounds.east }
        )
      : undefined,
    fields: ['formatted_address', 'geometry', 'place_id', 'address_components'],
  };

  return (
    <div className={`relative ${className}`}>
      <Autocomplete
        onLoad={handleLoad}
        onPlaceChanged={handlePlaceChanged}
        options={autocompleteOptions}
      >
        <div className="relative">
          {showIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
          )}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className={`
              block w-full rounded-md border border-gray-300 shadow-sm
              focus:border-indigo-500 focus:ring-indigo-500
              disabled:bg-gray-100 disabled:cursor-not-allowed
              ${showIcon ? 'pl-10' : 'pl-3'}
              pr-10 py-2
              ${inputClassName}
            `}
          />
          {inputValue && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </Autocomplete>
    </div>
  );
};

export default PlacesAutocomplete;

// Simplified address input with validation
interface AddressInputProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (place: PlaceResult) => void;
  error?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
}

export const AddressInput: React.FC<AddressInputProps> = ({
  value,
  onChange,
  onAddressSelect,
  error,
  label,
  required = false,
  disabled = false,
}) => {
  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <PlacesAutocomplete
        onPlaceSelect={(place) => {
          onChange(place.formattedAddress);
          onAddressSelect(place);
        }}
        defaultValue={value}
        disabled={disabled}
        inputClassName={error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};
